import * as React from 'react';
import * as Sebrae from '@sebrae/react-core';
import {
  FlexedListItem,
  AlignEnd,
  DiarioHeader,
} from './relatorio-diario-lista';
import {
  BaSeGridItem,
  BaSeSmall1,
  BaSeTheme,
  BaSeGrid,
  BaSeIcon,
  BaSeHeading5,
  BaSeBreakpointContext,
  BaSeButton,
  BaSeShapeButton,
  BaSeDialogContext,
} from '@base/react';
import { BodyCard, BodyCardCell } from '../relatorios-card-styled';
import GraficoLine from '../../shared/grafico/line';
import { toDate, compareAsc } from 'date-fns';
import { compareDesc } from 'date-fns/esm';
import { ShowButtonsAction } from '../head-buttons-action';
import { ModalShare } from '../modal-share';
import { useRelatorio } from '../../../services/relatorio/RelatorioService';

const DEFAULT_TEXT_COLOR = BaSeTheme.colors.institucionais.cinzaSebrae20;
const DEFAULT_ICON_COLOR = BaSeTheme.colors.institucionais.azulSebrae36;

const RelatorioDiarioRow = ({ dt1, dt2, dt3, dt4, end, isBlue, last }) => {
  const receita = Sebrae.useMask(dt2, 'decimal');
  const despesa = Sebrae.useMask(dt3, 'decimal');
  const resultado = Sebrae.useMask(dt4, 'decimal');
  const { inTablet } = React.useContext(BaSeBreakpointContext);

  const applyResultColor = (num) => {
    if (!num) {
      return DEFAULT_TEXT_COLOR;
    }
    return num > 0
      ? BaSeTheme.colors.feedbackInterface.sucesso26
      : BaSeTheme.colors.feedbackInterface.erro35;
  };

  const getCorrectCollNumber = React.useCallback(
    (val) => {
      return inTablet ? 3 : val ?? 4;
    },
    [inTablet],
  );

  return (
    <BodyCardCell end={end} isBlue={isBlue}>
      <BaSeGrid>
        <BaSeGridItem size={getCorrectCollNumber(1)}>
          <BaSeSmall1 isBold={last} color={DEFAULT_TEXT_COLOR}>
            {dt1}
          </BaSeSmall1>
        </BaSeGridItem>
        <BaSeGridItem size={getCorrectCollNumber(5)}>
          <AlignEnd>
            <BaSeSmall1
              color={
                dt2
                  ? BaSeTheme.colors.feedbackInterface.sucesso26
                  : DEFAULT_TEXT_COLOR
              }
            >
              R$ {receita}
            </BaSeSmall1>
          </AlignEnd>
        </BaSeGridItem>
        <BaSeGridItem size={getCorrectCollNumber(6)}>
          <AlignEnd>
            <BaSeSmall1
              color={
                dt3
                  ? BaSeTheme.colors.feedbackInterface.erro35
                  : DEFAULT_TEXT_COLOR
              }
            >
              {despesa !== '0,00' && '- '}
              R$ {despesa}
            </BaSeSmall1>
          </AlignEnd>
        </BaSeGridItem>
        {inTablet && (
          <BaSeGridItem size={3}>
            <AlignEnd>
              <BaSeSmall1 color={applyResultColor(dt4)}>
                {resultado?.includes('-') && '- '}
                R$ {resultado.replace('-', '')}
              </BaSeSmall1>
            </AlignEnd>
          </BaSeGridItem>
        )}
      </BaSeGrid>
    </BodyCardCell>
  );
};

export const RelatorioDiarioLista = ({
  dados,
  totalReceita,
  totalDespesa,
  original,
  download,
  loadingDownload,
  paramsFilter,
  isPublic = false,
}) => {
  const { inTablet } = React.useContext(BaSeBreakpointContext);
  const { compartilharRelatorio } = useRelatorio();
  const { showModal } = React.useContext(BaSeDialogContext);

  const [displayGraph, setDisplayGraph] = React.useState(false);
  const parentRef = React.useRef(null);

  const getDay = React.useCallback((lancamento) => {
    const [y, m, d] = lancamento?.data?.split('-');
    return `${d}/${m}/${y}`;
  }, []);

  const convertArrayToTable = React.useCallback(
    (lancamento) => ({
      ...lancamento,
      dt1: getDay(lancamento),
      dt2: parseFloat(lancamento?.valorTotalReceita).toFixed(2),
      dt3: parseFloat(lancamento?.valorTotalDespesa).toFixed(2),
      dt4: parseFloat(lancamento?.resultadoDia).toFixed(2),
    }),
    [getDay],
  );

  const [lancamentos, setLancamentos] = React.useState([]);
  const [filtro, setFiltro] = React.useState('dt1');
  const [filterDirection, setFilterDirection] = React.useState('asc');
  const handleSort = React.useCallback(
    (a, b) => {
      if (filtro === 'dt1') {
        const [d1, m1, y1] = a[filtro]?.split('/');
        const [d2, m2, y2] = b[filtro]?.split('/');
        const date1 = toDate(new Date(y1, m1, d1));
        const date2 = toDate(new Date(y2, m2, d2));
        if (filterDirection === 'asc') {
          return compareAsc(date1, date2);
        }
        return compareDesc(date1, date2);
      }
      const val = filterDirection === 'asc' ? [-1, 1] : [1, -1];
      return parseInt(a[filtro]) > parseInt(b[filtro]) ? val[0] : val[1];
    },
    [filtro, filterDirection],
  );

  const handleUpdateFilter = React.useCallback((prev, next) => {
    if (!prev) {
      setFilterDirection('asc');
      return next;
    }
    const handlePrevius = (p) => (p === 'asc' ? 'desc' : 'asc');
    setFilterDirection((previus) =>
      next === prev ? handlePrevius(previus) : 'asc',
    );
    return next;
  }, []);

  const handleShow = React.useCallback(() => {
    return (
      <>
        {lancamentos.sort(handleSort)?.map((ele, index) => (
          <RelatorioDiarioRow
            key={index}
            dt1={inTablet ? ele?.dt1 : ele?.dt1?.substr(0, 2)}
            dt2={ele?.dt2}
            dt3={ele?.dt3}
            dt4={ele?.dt4}
          />
        ))}
      </>
    );
  }, [handleSort, inTablet, lancamentos]);

  const handleShowGraphButton = React.useCallback(() => {
    const title = displayGraph ? 'Ver tabela' : 'Ver gráfico';
    const icon = displayGraph ? 'list-ul' : 'chart-line';
    return {
      title,
      icon,
    };
  }, [displayGraph]);

  const print = React.useCallback(() => window.print(), []);

  const showButtons = React.useMemo(() => {
    const { title, icon } = handleShowGraphButton();
    return inTablet ? (
      <BaSeButton
        value={title}
        leftIcon={icon}
        type="tertiary"
        onClick={() => setDisplayGraph((prev) => !prev)}
      />
    ) : (
      <BaSeShapeButton
        shape="square"
        nameIcon={icon}
        type="tertiary"
        onClick={() => setDisplayGraph((prev) => !prev)}
      />
    );
  }, [handleShowGraphButton, inTablet]);

  // eslint-disable-next-line
  const displayGraphicalData = React.useCallback(() => {
    if (displayGraph) {
      return (
        <>
          <GraficoLine lista={original} ref={parentRef} />
        </>
      );
    }
    return (
      <>
        <BodyCardCell>
          <BaSeGrid>
            <BaSeGridItem size={inTablet ? 3 : 1}>
              <FlexedListItem
                style={{ cursor: 'pointer' }}
                onClick={() =>
                  setFiltro((prev) => handleUpdateFilter(prev, 'dt1'))
                }
              >
                <BaSeSmall1 isBold={true} color={DEFAULT_TEXT_COLOR}>
                  Dia
                </BaSeSmall1>
                <BaSeIcon
                  name="arrow-head-down"
                  color={DEFAULT_ICON_COLOR}
                  size={12}
                  description="ordenar pelo índice"
                />
              </FlexedListItem>
            </BaSeGridItem>
            <BaSeGridItem size={inTablet ? 3 : 5}>
              <AlignEnd
                style={{ cursor: 'pointer' }}
                onClick={() =>
                  setFiltro((prev) => handleUpdateFilter(prev, 'dt2'))
                }
              >
                <FlexedListItem>
                  <BaSeSmall1 isBold={true} color={DEFAULT_TEXT_COLOR}>
                    Receita
                  </BaSeSmall1>
                  <BaSeIcon
                    name="arrow-head-down"
                    color={DEFAULT_ICON_COLOR}
                    size={12}
                    description="ordenar pela receita"
                  />
                </FlexedListItem>
              </AlignEnd>
            </BaSeGridItem>
            <BaSeGridItem size={inTablet ? 3 : 6}>
              <AlignEnd
                style={{ cursor: 'pointer' }}
                onClick={() =>
                  setFiltro((prev) => handleUpdateFilter(prev, 'dt3'))
                }
              >
                <FlexedListItem>
                  <BaSeSmall1 isBold={true} color={DEFAULT_TEXT_COLOR}>
                    Despesa
                  </BaSeSmall1>
                  <BaSeIcon
                    name="arrow-head-down"
                    color={DEFAULT_ICON_COLOR}
                    size={12}
                    description="ordenar pela despesa"
                  />
                </FlexedListItem>
              </AlignEnd>
            </BaSeGridItem>
            {inTablet && (
              <BaSeGridItem size={3}>
                <AlignEnd>
                  <BaSeSmall1 isBold={true} color={DEFAULT_TEXT_COLOR}>
                    Resultado do dia
                  </BaSeSmall1>
                </AlignEnd>
              </BaSeGridItem>
            )}
          </BaSeGrid>
        </BodyCardCell>
        {handleShow()}
        <RelatorioDiarioRow
          isBlue={true}
          end={1}
          last={true}
          dt1={inTablet ? 'Total do período' : 'Total'}
          dt2={parseFloat(totalReceita).toFixed(2)}
          dt3={parseFloat(totalDespesa).toFixed(2)}
          dt4={parseFloat(totalReceita - totalDespesa).toFixed(2)}
        />
      </>
    );
  }, [
    displayGraph,
    inTablet,
    handleShow,
    totalReceita,
    totalDespesa,
    original,
    handleUpdateFilter,
  ]);

  React.useEffect(() => {
    setLancamentos(dados.map(convertArrayToTable));
  }, [convertArrayToTable, dados]);

  const handleShowModal = React.useCallback(
    () =>
      showModal({
        title: 'Compartilhar',
        actions: [],
        size: 'medium',
        content: ({ closeModal }) => (
          <ModalShare
            closeModal={closeModal}
            filters={paramsFilter}
            tipoRelatorio={'RELATORIO_DIARIO'}
            callback={compartilharRelatorio}
          />
        ),
      }),
    [paramsFilter, compartilharRelatorio, showModal],
  );

  return (
    <BodyCard ref={parentRef}>
      <BodyCardCell start={1}>
        <DiarioHeader full>
          <BaSeHeading5 isBold color="#192A3E">
            Relatório diário
          </BaSeHeading5>
          <DiarioHeader>
            {!isPublic && (
              <>
                {showButtons}
                <ShowButtonsAction
                  download={download}
                  loadingDownload={loadingDownload}
                  print={print}
                  isDisabled={false}
                  showModal={handleShowModal}
                />
              </>
            )}
            {isPublic &&
              (inTablet ? (
                <BaSeButton
                  value="Imprimir"
                  leftIcon="print"
                  onClick={print}
                  type="secondary"
                />
              ) : (
                <BaSeShapeButton
                  shape="square"
                  nameIcon="print"
                  onClick={print}
                  type="secondary"
                />
              ))}
          </DiarioHeader>
        </DiarioHeader>
      </BodyCardCell>
      {displayGraphicalData()}
    </BodyCard>
  );
};
